<template>
  <div class="controls">
    <audio ref="audio" :title="bookTitle">
      <template v-for="source in sources">
        <source :src="source.href" :type="source.type"></source>
      </template>
    </audio>

    <button class="controls__button" id="previous-track" @click="previousTrack" :title="t_previous_chapter" :aria-label="t_previous_chapter">
      <icon name="previous-track" aria-hidden="true"></icon>
    </button>
    <button class="controls__button" id="rewind" @click="quickRewind" :title="t_rewind" :aria-label="t_rewind">
      <icon name="backward" aria-hidden="true"></icon>
    </button>
    <button class="controls__button controls__playback" @click="togglePlayback" :title="t_play_or_pause" :aria-label="t_play_or_pause">
      <!-- Fix Safari icon alignment -->
      <div aria-hidden="true">
        <icon :name="playbackButtonState" :class="playbackButtonState"></icon>
      </div>
    </button>
    <button class="controls__button" id="fastforward" @click="quickFastForward" :title="t_skip_secs" :aria-label="t_skip_secs">
      <icon name="forward" aria-hidden="true"></icon>
    </button>
    <button class="controls__button" id="next-track" @click="nextTrack" :title="t_next_chapter" :aria-label="t_next_chapter">
      <icon name="next-track" aria-hidden="true"></icon>
    </button>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
export default {
  props: {
    sources: {
      default: []
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    currentTime: {
      type: Number,
      default: 0.0
    },
    initialTime: {
      type: Number,
      default: 0.0
    },
    trackEnded: {
      type: Function
    },
    volume: {
      type: Number,
      default: 0.5
    },
    playbackRate: {
      type: Number,
      default: 1
    },
    bookTitle: {
      type: String,
      default: ''
    },
    isLastTrack: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      playing: false
    }
  },

  computed: {
    playbackButtonState () {
      return this.playing ? 'pause' : 'play'
    },
    t_previous_chapter () {
      return this.$t('previous_chapter')
    },
    t_rewind () {
      return this.$t('rewind_secs', { secs: 30 })
    },
    t_play_or_pause () {
      return this.$t(this.playbackButtonState)
    },
    t_skip_secs () {
      return this.$t('skip_secs', { secs: 30 })
    },
    t_next_chapter () {
      return this.$t('next_chapter')
    },
  },

  mounted () {
    let audio = this.$refs.audio
    this.element = audio
    if (this.autoplay) {
      audio.play()
    }

    if (this.initialTime !== 0) {
      let handler = function () {
        this.$refs.audio.removeEventListener('canplay', handler);
        (this.$refs.audio.currentTime = this.initialTime)
      }.bind(this)
      audio.addEventListener('canplay', handler)
    }

    audio.playbackRate = this.playbackRate
    audio.volume = this.volume

    // Not working for Flatland:
    // https://hadriengardeur.github.io/audiobook-manifest/examples/flatland.audiobook-manifest
    if (this.trackEnded != null) {
      audio.addEventListener('ended', () => {
        this.trackEnded()

        if (this.isLastTrack) {
          if (audio.paused) {
            audio.removeEventListener('timeupdate', this.emitCurrentTime)
            this.playing = false
          }
          this.$emit('bookended')
        }
      })
    }
  },

  watch: {
    sources (srcs) {
      let audio = this.$refs.audio

      audio.load()

      if (this.playing) {
        audio.play()
      }

      this.emitCurrentTime()
      audio.playbackRate = this.playbackRate
      audio.volume = this.volume
    },

    currentTime (newCurrentTime) {
      if (!this.playing) {
        Array.from(this.$refs.audio.getElementsByTagName('source')).forEach((el) => { el.src += '#t=' + newCurrentTime })
      }

      // Avoid jitter because of the loop between the prop and the audio element value
      if (Math.abs(this.$refs.audio.currentTime - newCurrentTime) > 0.5) {
        this.$refs.audio.currentTime = newCurrentTime
      }
    },

    playbackRate (newRate) {
      this.$refs.audio.playbackRate = newRate
    },

    volume (newVolume) {
      this.$refs.audio.volume = newVolume
    }
  },

  methods: {
    emitCurrentTime () {
      this.$emit('timeupdate', this.$refs.audio.currentTime)
    },

    togglePlayback () {
      let audio = this.$refs.audio
      if (this.playing) {
        audio.removeEventListener('timeupdate', this.emitCurrentTime)
        audio.pause()
      } else {
        audio.addEventListener('timeupdate', this.emitCurrentTime)
        audio.play()
      }
      this.playing = !this.playing
      this.setPlaying(this.playing)
    },

    ...mapActions(['quickRewind', 'quickFastForward', 'nextTrack', 'previousTrack', 'setPlaying'])
  }
}
</script>

<style>
  .controls {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .controls__button {
    padding: 1em;
    cursor: pointer;
    width: 4em;
    height: 4em;
  }

  .controls__button svg {
    height: 100%;
    width: 100%;
  }

  .controls__playback {
    color: #fff;
    border: 1px solid rgba(255,255,255, 0.85);
    border-radius: 99px;
    width: 4.5em;
    height: 4.5em;
    padding: 1.25em;
    margin-left:  1.25em;
    margin-right: 1.25em;
  }

  .controls__playback .play {
    margin-right: -0.45em;
  }

  .controls__playback:hover {
    border-color: #fff;
  }
</style>



// WEBPACK FOOTER //
// src/components/AudioControls.vue